import React, { useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { Formik, FormikHelpers, FormikProps, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import merge from 'lodash.merge';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';

import { sortCountries } from 'helpers/helpers';
import compose from 'helpers/compose';
import DatePicker from 'components/FormFields/FormDatePicker';
import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormPhoneField from 'components/FormFields/FormPhoneField';
import CustomPaper from 'components/Layout/CustomPaper';
import ControlButtons from 'components/ControlButtons';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import Switch from 'components/FormFields/Switch';
import ClientSearch from 'components/ClientSearch/ClientSearch';

import { EditAccountStore } from 'stores/editAccountStore';
import { WorkflowBasicStore } from 'stores/workflowStores/workflowBasicStore/workflowBasicStore';
import {
  AccountEditBasic,
  getInitialValues,
  AccountClientSelectable,
} from 'stores/workflowStores/workflowBasicStore/model';
import { AccountStatus, PersonType } from 'enums';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { ACCOUNTS_PAGE, ACCOUNTS_DETAIL, ACCOUNTS_COMPLETE } from 'routes';
import { RootStore } from 'stores/rootStore';

import useStyles from './styles';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  rootStore: RootStore;
  editAccountStore: EditAccountStore;
}

const AccountBasic: React.FC<Props> = ({ editAccountStore, rootStore }) => {
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const classes = useStyles();
  const [store] = useState(new WorkflowBasicStore(rootStore, accountId));
  const { data } = store;
  const clientSelectable = rootStore.stores.editAccountStore.data?.selectable;

  useEffect(() => {
    editAccountStore.setStep(0);
    store.getAccountBasic();
  }, [editAccountStore.data, editAccountStore, store]);

  const submit = (values: AccountEditBasic, formikHelpers: FormikHelpers<AccountEditBasic>) => {
    store.handleSubmitBasic(values, formikHelpers);
  };

  const renderGrid = (formikProps: FormikProps<AccountEditBasic>) => (
    <Grid container className={classes.form}>
      {clientSelectable !== AccountClientSelectable.UNAVAILABLE && (
        <ClientSearch
          accountId={accountId}
          personType={formikProps.values.client.personType}
          refresh={() => store.getAccountBasic(formikProps.values.title)}
        />
      )}

      <TextField name="personType" type="hidden" style={{ visibility: 'hidden' }} />

      <Grid item md={8} xs={12}>
        <FormTextField
          name="title"
          label={t('general_accountName')}
          tooltipContent={t('tooltip_accountBasic_accountName')}
          afterChange={() => rootStore.stores.uiStore.updateNoteTitle(formikProps.values.title)}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <FormTextField name="currency" label={t('general_currency')} disabled />
      </Grid>
      {formikProps.values.client.personType === PersonType.PHYSICAL ? (
        <>
          <Grid item xs={12} md={6}>
            <FormTextField name="client.firstname" label={t('general_clientName')} readOnly={data?.readOnly} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormTextField name="client.lastname" label={t('general_surname')} readOnly={data?.readOnly} />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormSelectField
              name="client.gender"
              label={t('general_gender')}
              readOnly={data?.readOnly}
              options={formikProps.values.options.gender.map((gender) => ({
                value: gender,
                label: t(`gender_${gender}`),
              }))}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DatePicker
              maxDate={DateTime.local().minus({ years: 18 })} // * it still shows now-17 year, but it can't be used
              minDate={DateTime.local(1900, 1, 1)}
              name="client.birthDate"
              label={t('general_birthDate')}
              readOnly={data?.readOnly}
            />
          </Grid>

          <Grid item xs={6}>
            <Switch
              name="client.retired"
              label={t('accountBasic_retirement')}
              tooltipContent={t('tooltip_accountBasic_retirement')}
            />
          </Grid>
          <Grid item xs={6} />
          <Grid item xs={6}>
            <Switch
              name="client.fixedTax"
              label={t('accountBasic_fixedTax')}
              tooltipContent={
                <span
                  dangerouslySetInnerHTML={{
                    __html: t('tooltip_accountBasic_fixedTax', {
                      interpolation: { escapeValue: false },
                    }),
                  }}
                />
              }
            />
          </Grid>
          <Grid item xs={6} />
        </>
      ) : (
        <>
          <Grid item xs={12} md={12}>
            <FormTextField name="client.companyName" label={t('general_companyName')} readOnly={data?.readOnly} />
          </Grid>
        </>
      )}

      <Grid item xs={12} md={6}>
        <FormTextField name="email" label={t('general_email')} type="email" readOnly={data?.readOnly} />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormPhoneField name="phone" label={t('general_phone')} disabled={data?.readOnly} />
      </Grid>

      <Grid item xs={12} md={6}>
        {data?.readOnly && !!data.client.taxResidency.country ? (
          <FormTextField
            name="client.taxResidency.country"
            label={t('contract_taxInfo_taxResidency')}
            value={t(`country_${formikProps.values.client.taxResidency.country}`)}
            readOnly
          />
        ) : (
          <FormSelectField
            name="client.taxResidency.country"
            label={t('contract_taxInfo_taxResidency')}
            options={sortCountries(
              formikProps.values.client.options!.taxResidencyCountry.map((code) => ({
                value: code,
                label: t(`country_${code}`),
              })),
            )}
          />
        )}
      </Grid>
    </Grid>
  );

  if (!data) {
    return null;
  }

  const { accountStatus, accountProcessing } = data;

  // Redirect to correct pages for certain statuses
  if (accountStatus === AccountStatus.ACTIVE || accountStatus === AccountStatus.AWAITING_APPROVAL) {
    return <Redirect to={ACCOUNTS_DETAIL(accountId)} />;
  }

  if (accountStatus === AccountStatus.TO_BE_SIGNED || accountStatus === AccountStatus.CANCELLED) {
    return <Redirect to={ACCOUNTS_PAGE} />;
  }

  if (accountStatus === AccountStatus.DRAFT && accountProcessing) {
    return <Redirect to={ACCOUNTS_COMPLETE(accountId)} />;
  }

  const initialValues = getInitialValues(data.client.personType);

  return data ? (
    <Formik initialValues={merge({}, initialValues, data)} onSubmit={submit} enableReinitialize>
      {(formikProps) => (
        <FormWithoutEnterSubmit>
          <Container maxWidth="md">
            <CustomPaper title={t('accountStepper_basic')} classes={classes}>
              {renderGrid(formikProps)}
            </CustomPaper>
          </Container>

          <ControlButtons
            isSubmitting={formikProps.isSubmitting || formikProps.isValidating}
            nextText={t('accountStepper_variantChooser')}
            hideBack
          />
        </FormWithoutEnterSubmit>
      )}
    </Formik>
  ) : null;
};

export default compose(inject('editAccountStore', 'rootStore'), observer)(AccountBasic);
